/**
 * Session State Store (Zustand)
 *
 * Centralized state management for session-related data:
 * - Messages
 * - Players
 * - Turn management
 * - Roll management
 * - UI state
 */

import { create } from "zustand";
import {
  DisplayMessage,
  PlayerListItem,
  RequestRollPayload,
} from "../types/session";

interface SessionState {
  // Messages
  messages: DisplayMessage[];

  // Players
  playerList: PlayerListItem[];
  playerScores: Record<string, number>;

  // Turn management
  isTurnBased: boolean;
  currentTurnUserId: string | null;
  currentTurnDisplayName: string | null;

  // Roll management
  pendingRoll: RequestRollPayload | null;

  // UI state
  collapsedSections: Record<string, boolean>;
  isToolbarVisible: boolean;

  // Actions - Messages
  addMessage: (message: DisplayMessage) => void;
  setMessages: (messages: DisplayMessage[]) => void;
  clearMessages: () => void;

  // Actions - Players
  setPlayerList: (playerList: PlayerListItem[]) => void;
  updatePlayerList: (
    updates:
      | PlayerListItem[]
      | { type: "remove"; userId: string }
      | {
          type: "update";
          userId: string;
          field: "isReady" | "displayName";
          value: boolean | string;
        }
  ) => void;
  setPlayerScores: (scores: Record<string, number>) => void;
  updatePlayerScore: (userId: string, score: number) => void;

  // Actions - Turn management
  setIsTurnBased: (isTurnBased: boolean) => void;
  setCurrentTurn: (userId: string | null, displayName: string | null) => void;

  // Actions - Roll management
  setPendingRoll: (roll: RequestRollPayload | null) => void;
  clearPendingRoll: () => void;

  // Actions - UI state
  toggleSectionCollapse: (timestamp: string) => void;
  setIsToolbarVisible: (visible: boolean) => void;

  // Actions - Reset/Initialize
  initialize: (options?: {
    isTurnBased?: boolean;
    playerScores?: Record<string, number>;
  }) => void;
  reset: () => void;
}

const initialState = {
  messages: [],
  playerList: [],
  playerScores: {},
  isTurnBased: false,
  currentTurnUserId: null,
  currentTurnDisplayName: null,
  pendingRoll: null,
  collapsedSections: {},
  isToolbarVisible: true,
};

export const useSessionStore = create<SessionState>((set, get) => ({
  ...initialState,

  // Messages
  addMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, message],
    })),

  setMessages: (messages) => set({ messages }),

  clearMessages: () => set({ messages: [] }),

  // Players
  setPlayerList: (playerList) => {
    // Extract scores from player list
    const playerScores: Record<string, number> = {};
    playerList.forEach((player) => {
      playerScores[player.userId] = player.xp ?? 0;
    });

    set({ playerList, playerScores });
  },

  updatePlayerList: (updates) => {
    const state = get();

    if (Array.isArray(updates)) {
      // Replace entire list (deduplicate by userId)
      const uniquePlayerMap = new Map<string, PlayerListItem>();
      updates.forEach((player) => {
        uniquePlayerMap.set(player.userId, player);
      });
      const uniquePlayerList = Array.from(uniquePlayerMap.values());

      // Extract scores
      const playerScores: Record<string, number> = {};
      uniquePlayerList.forEach((player) => {
        playerScores[player.userId] = player.xp ?? 0;
      });

      set({ playerList: uniquePlayerList, playerScores });
    } else if (updates.type === "remove") {
      // Remove player
      const filteredList = state.playerList.filter(
        (p) => p.userId !== updates.userId
      );
      const { [updates.userId]: _removed, ...remainingScores } =
        state.playerScores;
      set({ playerList: filteredList, playerScores: remainingScores });
    } else if (updates.type === "update") {
      // Update specific field
      const updatedList = state.playerList.map((player) => {
        if (player.userId === updates.userId) {
          return {
            ...player,
            [updates.field]: updates.value,
          };
        }
        return player;
      });
      set({ playerList: updatedList });
    }
  },

  setPlayerScores: (scores) => set({ playerScores: scores }),

  updatePlayerScore: (userId, score) =>
    set((state) => ({
      playerScores: {
        ...state.playerScores,
        [userId]: score,
      },
    })),

  // Turn management
  setIsTurnBased: (isTurnBased) => set({ isTurnBased }),

  setCurrentTurn: (userId, displayName) =>
    set({
      currentTurnUserId: userId,
      currentTurnDisplayName: displayName,
    }),

  // Roll management
  setPendingRoll: (roll) => set({ pendingRoll: roll }),

  clearPendingRoll: () => set({ pendingRoll: null }),

  // UI state
  toggleSectionCollapse: (timestamp) =>
    set((state) => ({
      collapsedSections: {
        ...state.collapsedSections,
        [timestamp]: !state.collapsedSections[timestamp],
      },
    })),

  setIsToolbarVisible: (visible) => set({ isToolbarVisible: visible }),

  // Initialize with initial values
  initialize: (options) =>
    set((state) => ({
      ...state,
      isTurnBased: options?.isTurnBased ?? state.isTurnBased,
      playerScores: options?.playerScores ?? state.playerScores,
    })),

  // Reset to initial state
  reset: () => set(initialState),
}));
